import { useId, type FormEvent } from "react";
import type { AmendInput, PrintedVesselCall } from "@/api/invoices";
import { RequestError } from "@/components/RequestError";
import { TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import {
  EXTRA_NOTES_HINT,
  STANDARD_DISCOUNT_NOTE,
  notesFromText,
} from "@/components/forms/InvoiceDraftForm";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type AmendInvoiceFormProps = {
  initial: PrintedVesselCall;
  /** The printed discount, if the invoice has one: its note can be reworded. */
  discount: { description: string; note?: string } | null;
  extraNotes: string[];
  onSubmit: (input: AmendInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** Corrects the vessel-call details and the wording of the notes printed on an issued
 * invoice; a new PDF version is kept beside the original. */
export function AmendInvoiceForm({
  initial,
  discount,
  extraNotes,
  onSubmit,
  onCancel,
  onReload,
}: AmendInvoiceFormProps) {
  const id = useId();
  const notes = useFormValues({
    discount_note: discount?.note ?? "",
    extra_notes: extraNotes.join("\n"),
  });
  const { values, set } = useFormValues<AmendInput>({
    reason: "",
    voyage: initial.voyage,
    terminal: initial.terminal,
    port: initial.port,
    arrival: initial.arrival,
    departure: initial.departure,
    service_from: initial.service_from,
    service_to: initial.service_to,
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        ...values,
        reason: values.reason.trim(),
        voyage: values.voyage.trim(),
        terminal: values.terminal.trim(),
        port: values.port.trim(),
        ...(discount ? { discount_note: notes.values.discount_note.trim() } : {}),
        extra_notes: notesFromText(notes.values.extra_notes),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        The number, date, client, items and amounts stay as issued - correct those with a credit
        note, or cancel and issue again. The corrected PDF is stored as a new version and the
        original is kept.
      </p>
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-voyage`}
          label="Voyage"
          value={values.voyage}
          onChange={(value) => set("voyage", value)}
          error={errors.voyage}
        />
        <TextField
          id={`${id}-terminal`}
          label="Terminal"
          value={values.terminal}
          onChange={(value) => set("terminal", value)}
          error={errors.terminal}
        />
        <TextField
          id={`${id}-port`}
          label="Port"
          value={values.port}
          onChange={(value) => set("port", value)}
          error={errors.port}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-arrival`}
          label="Arrival"
          type="date"
          value={values.arrival}
          onChange={(value) => set("arrival", value)}
          error={errors.arrival}
        />
        <TextField
          id={`${id}-departure`}
          label="Departure"
          type="date"
          value={values.departure}
          onChange={(value) => set("departure", value)}
          error={errors.departure}
        />
        <TextField
          id={`${id}-service-from`}
          label="Planning started"
          type="date"
          value={values.service_from}
          onChange={(value) => set("service_from", value)}
          error={errors.service_from}
        />
        <TextField
          id={`${id}-service-to`}
          label="Planning ended"
          type="date"
          value={values.service_to}
          onChange={(value) => set("service_to", value)}
          error={errors.service_to}
        />
        {discount && (
          <TextAreaField
            id={`${id}-discount-note`}
            label="Discount note"
            value={notes.values.discount_note}
            onChange={(value) => notes.set("discount_note", value)}
            error={errors.discount_note}
            hint={`About "${discount.description}". ${STANDARD_DISCOUNT_NOTE}`}
            rows={2}
            className="md:col-span-2"
          />
        )}
        <TextAreaField
          id={`${id}-extra-notes`}
          label="Additional notes"
          value={notes.values.extra_notes}
          onChange={(value) => notes.set("extra_notes", value)}
          error={errors.extra_notes}
          hint={EXTRA_NOTES_HINT}
          rows={2}
          className="md:col-span-2"
        />
        <TextAreaField
          id={`${id}-reason`}
          label="Reason for the amendment"
          value={values.reason}
          onChange={(value) => set("reason", value)}
          error={errors.reason}
          hint="Kept in the audit log, e.g. voyage number confirmed by the agent"
          rows={2}
          className="md:col-span-2"
        />
      </div>
      <FormActions saving={saving} submitLabel="Store corrected PDF" onCancel={onCancel} />
    </form>
  );
}
